import { useState, useEffect, useCallback } from 'react';
import { Tag, Search, Plus, RefreshCw, Edit2, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { taxApi } from '@/api/tax.api';
import type { TaxType } from '@/api/tax.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { Combobox } from '@/components/ui/Combobox';
import { cn } from '@/utils/cn';

const TAX_CATEGORIES = ['VAT', 'GST', 'SalesTax', 'WithholdingTax', 'ServiceTax', 'ExciseTax', 'ReverseCharge', 'ImportDuty', 'Other'];

// ── Dialog ────────────────────────────────────────────────────────────────────

interface TypeDialogProps {
  open: boolean;
  record: TaxType | null;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY: Partial<TaxType> = {
  typeCode: '', typeName: '', taxCategory: 'VAT',
  isRecoverable: false, isWithholding: false, isReverseCharge: false,
  isActive: true, remarks: null,
};

const TypeDialog = ({ open, record, onClose, onSaved }: TypeDialogProps) => {
  const [form, setForm] = useState<Partial<TaxType>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setForm(record ? { ...record } : { ...EMPTY }); setError(null); }, [record, open]);

  if (!open) return null;

  const set = (k: keyof TaxType, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.typeCode?.trim() || !form.typeName?.trim() || !form.taxCategory) {
      setError('Type Code, Type Name and Tax Category are required.'); return;
    }
    setSaving(true); setError(null);
    try {
      if (record) { await taxApi.updateType(record.id, form); }
      else { await taxApi.createType(form); }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      setError(msg);
    } finally { setSaving(false); }
  };

  const inputCls = 'w-full h-9 px-3 rounded-md border text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500';
  const labelCls = 'block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1';

  const BoolChip = ({ k, label }: { k: 'isRecoverable' | 'isWithholding' | 'isReverseCharge'; label: string }) => (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
      <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">{record ? 'Edit Tax Type' : 'New Tax Type'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {error && <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Type Code *</label>
              <input className={inputCls} value={form.typeCode ?? ''} onChange={e => set('typeCode', e.target.value)} maxLength={20} />
            </div>
            <div>
              <label className={labelCls}>Tax Category *</label>
              <Combobox value={form.taxCategory ?? 'VAT'} onChange={v => set('taxCategory', v)} clearable={false}
                options={TAX_CATEGORIES.map(c => ({ value: c, label: c }))} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Type Name *</label>
            <input className={inputCls} value={form.typeName ?? ''} onChange={e => set('typeName', e.target.value)} maxLength={150} />
          </div>
          <div className="flex items-center gap-4">
            <BoolChip k="isRecoverable" label="Recoverable" />
            <BoolChip k="isWithholding" label="Withholding" />
            <BoolChip k="isReverseCharge" label="Reverse Charge" />
          </div>
          {record && (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={!!form.isActive} onChange={e => set('isActive', e.target.checked)} /> Active
            </label>
          )}
          <div>
            <label className={labelCls}>Remarks</label>
            <textarea className="w-full px-3 py-2 rounded-md border text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
              rows={2} value={form.remarks ?? ''} onChange={e => set('remarks', e.target.value || null)} maxLength={500} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="h-8 px-4 text-xs rounded-md bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
            {saving && <Spinner size="sm" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Bool chip ─────────────────────────────────────────────────────────────────
const BoolBadge = ({ val, labelTrue = 'Yes', labelFalse = 'No' }: { val: boolean; labelTrue?: string; labelFalse?: string }) => (
  <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
    val ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
        : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400')}>
    {val ? labelTrue : labelFalse}
  </span>
);

// ── Page ──────────────────────────────────────────────────────────────────────

export const TaxTypesPage = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.TAX_TYPE_CREATE as string);
  const canEdit   = hasPermission(Permissions.TAX_TYPE_EDIT as string);
  const canDelete = hasPermission(Permissions.TAX_TYPE_DELETE as string);

  const [items, setItems]     = useState<TaxType[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);
  const pageSize = 20;

  const [search, setSearch]           = useState('');
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editRecord, setEditRecord]   = useState<TaxType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TaxType | null>(null);
  const [deleting, setDeleting]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await taxApi.getTypes({ search: search || undefined, pageNumber: page, pageSize });
      const d = res.data?.data;
      setItems(d?.items ?? []);
      setTotal(d?.totalCount ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => { setDialogOpen(false); setEditRecord(null); load(); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await taxApi.deleteType(deleteTarget.id); setDeleteTarget(null); load(); }
    catch { /* silent */ }
    finally { setDeleting(false); }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Tag size={20} className="text-brand-600" /> Tax Types
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{total.toLocaleString()} records</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"><RefreshCw size={16} /></button>
            {canCreate && (
              <button onClick={() => { setEditRecord(null); setDialogOpen(true); }}
                className="flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Plus size={15} /> New Type
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search code or name…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Tag size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No tax types found</p>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                {['Code', 'Name', 'Category', 'Recoverable', 'Withholding', 'Reverse Charge', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(row => (
                <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono font-semibold text-brand-700 dark:text-brand-400">{row.typeCode}</td>
                  <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200">{row.typeName}</td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{row.taxCategory}</span>
                  </td>
                  <td className="px-4 py-2.5"><BoolBadge val={row.isRecoverable} /></td>
                  <td className="px-4 py-2.5"><BoolBadge val={row.isWithholding} /></td>
                  <td className="px-4 py-2.5"><BoolBadge val={row.isReverseCharge} /></td>
                  <td className="px-4 py-2.5">
                    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      row.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400')}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1">
                      {canEdit && <button onClick={() => { setEditRecord(row); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-500 transition-colors"><Edit2 size={13} /></button>}
                      {canDelete && <button onClick={() => setDeleteTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-red-500 transition-colors"><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">Page {page} of {totalPages} · {total.toLocaleString()} records</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      <TypeDialog open={dialogOpen} record={editRecord} onClose={() => { setDialogOpen(false); setEditRecord(null); }} onSaved={handleSaved} />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Delete Tax Type?</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Delete <strong>{deleteTarget.typeName}</strong>? This cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="h-8 px-4 text-xs rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50">Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="h-8 px-4 text-xs rounded-md bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50 flex items-center gap-1.5">
                {deleting && <Spinner size="sm" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
